import React, { useState, useContext } from 'react';
import ComboDisplay from '../ComboDisplay/ComboDisplay';
import AbilityButtons from '../AbilityButtons/AbilityButtons';
import CombatDisplay from './CombatDisplay';
import CooldownDebugTable from './CooldownDebugTable';
import MiniAbilityIcons from './MiniAbilityIcons';
import { SimplifiedChampion, SimplifiedSpell } from '../../../../../gameTypes';
import { GameLog } from '../../../Logs/LogsDisplay';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import SocketContext from '../../../../../../NewWebSocket/components/WebSocketAdapter';
import { getDamageTypeIcon, DamageSubType } from '../ComboDisplay/Events/utils';
import { getUniversalSourceIcon } from '../ComboDisplay/Events/StatChangeEvent';
import './CustomComboSection.css';
import '../../../../../../NewWebSocket/components/SimpleMatchupDisplay.css'; // Import the better styling
import PassiveBar from '../PassiveBar/PassiveBar';

// Helper function to extract base ability from duration-enhanced sequences
const extractBaseAbility = (skill: string): string => {
  // Handle wait/delay notation (_:1.5 or just _)
  if (skill.startsWith('_')) {
    return '_';
  }
  
  // Handle cast time break (T:0.5)
  if (skill.startsWith('T:')) {
    return 'T';
  }
  
  // Handle blocking hold notation (E[3], Q[max], R[min])
  const blockingHoldMatch = skill.match(/^([QWERA])\[/);
  if (blockingHoldMatch) {
    return blockingHoldMatch[1];
  }
  
  // Handle non-blocking toggle notation (R::4, Q::max)
  const nonBlockingToggleMatch = skill.match(/^([QWERA])::/);
  if (nonBlockingToggleMatch) {
    return nonBlockingToggleMatch[1];
  }
  
  // Handle explicit recast format (E:3E, Q:2Q, etc.) — legacy
  const explicitRecastMatch = skill.match(/^([QWERA]):(\d+\.?\d*|\w+)\1$/);
  if (explicitRecastMatch) {
    return explicitRecastMatch[1];
  }
  
  // Handle duration format (E:3, Q:max, E:min, etc.) — legacy
  if (skill.includes(':')) {
    const [ability] = skill.split(':');
    if (['Q', 'W', 'E', 'R', 'A'].includes(ability)) {
      return ability;
    }
  }
  
  // Return as-is if no duration syntax found
  return skill;
};

interface CustomComboSectionProps {
  champion: SimplifiedChampion;
  logs: GameLog[];
  targetChampion: SimplifiedChampion | null;
  onAbilityClick: (abilityKey: string) => void;
  onResetCombo?: () => void;
  spellImageUrls: Record<string, string>;
  autoAttackImageUrl: string;
  passiveImageUrl: string;
  championImageUrl?: string;
  targetChampionImageUrl?: string;
}

const CustomComboSection: React.FC<CustomComboSectionProps> = ({
  champion,
  logs,
  targetChampion,
  onAbilityClick,
  onResetCombo,
  spellImageUrls,
  autoAttackImageUrl,
  passiveImageUrl,
  championImageUrl,
  targetChampionImageUrl,
}) => {
  const [showComboBreakdown, setShowComboBreakdown] = useState(false);
  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);
  const socket = useContext(SocketContext);

  // Debug logging (condensed)
  console.log('[CustomComboSection] Displaying:', {
    champion: champion?.name,
    targetChampion: targetChampion?.name,
    logsLength: logs?.length || 0
  });

  // Calculate damage breakdown from logs (similar to DamageBreakdown.tsx)
  const calculateDamageBreakdown = (logs: GameLog[], targetChampion: SimplifiedChampion | null) => {
    if (!targetChampion) {
      return {
        total: 0,
        damageByType: { physical: 0, magic: 0, trueDamage: 0 },
        percentage: 0
      };
    }

    const damageByType = {
      physical: 0,
      magic: 0,
      trueDamage: 0,
    };

    let totalDamage = 0;

    // Calculate total damage taken by the target champion, only from this champion
    logs.forEach((log) => {
      if (log.type === 'DamageTaken' && log.details.championId === targetChampion.id && log.details.attackerId === champion?.id) {
        const damageAmount = log.details.damageAmount || 0;
        totalDamage += damageAmount;

        // Determine damage subtype
        const damageSubType = log.details.damageSubType;
        switch (damageSubType) {
          case DamageSubType.Physical:
            damageByType.physical += damageAmount;
            break;
          case DamageSubType.Magic:
            damageByType.magic += damageAmount;
            break;
          case DamageSubType.True:
            damageByType.trueDamage += damageAmount;
            break;
          default:
            break;
        }
      }
    });

    const targetMaxHealth = targetChampion.stats.totalStats.health;
    const percentage = targetMaxHealth > 0 ? (totalDamage / targetMaxHealth) * 100 : 0;

    return { total: totalDamage, damageByType, percentage };
  };

  // Helper function to extract image URL from universal icon function
  const getImageUrlFromUniversalIcon = (iconRequest: any): string | null => {
    const { sourceType, imageState, championName, abilityKey } = iconRequest;
    const { championImages } = require('../ComboDisplay/Events/Images/championImages');
    
    if ((sourceType === 'Ability' || sourceType === 'Passive') && championName) {
      const champMap = championImages[championName];
      if (champMap) {
        let finalIconUrl: string | undefined;

        if (sourceType === 'Ability') {
          // For Ability sources, try imageState first
          if (imageState && imageState !== 'default') {
            finalIconUrl = champMap[imageState.toLowerCase()];
          }
          
          // Try championImages with abilityKey if no imageState result
          if (!finalIconUrl && abilityKey) {
            finalIconUrl = champMap[abilityKey.toLowerCase()];
          }

          // Fallback to championsAssets for abilities
          if (!finalIconUrl && championsAssets[championName]?.spellImageUrls && imageState) {
            finalIconUrl = championsAssets[championName].spellImageUrls[imageState.toUpperCase()];
          }
        }

        if (finalIconUrl) {
          return finalIconUrl;
        }
      }
    }
    
    return null;
  };

  // Extract ability sequence from logs as a string for MiniAbilityIcons
  const extractAbilitySequenceString = (logs: GameLog[]): string => {
    const abilities = logs
      .filter(log => log.type === 'KeyPress')
      .map(log => {
        const abilityKey = log.details.abilityKey || '';
        
        let normalizedKey = '';
        if (abilityKey.toLowerCase() === 'q') normalizedKey = 'Q';
        else if (abilityKey.toLowerCase() === 'w') normalizedKey = 'W';
        else if (abilityKey.toLowerCase() === 'e') normalizedKey = 'E';
        else if (abilityKey.toLowerCase() === 'r') normalizedKey = 'R';
        else if (abilityKey.toLowerCase() === 'a' || abilityKey.toLowerCase() === 'aa') normalizedKey = 'A';
        else normalizedKey = abilityKey.toUpperCase();
        
        return normalizedKey;
      })
      .filter(ability => ability); // Remove empty abilities
    
    return abilities.join(' ');
  };

  const damageData = calculateDamageBreakdown(logs, targetChampion);
  const abilitySequenceString = extractAbilitySequenceString(logs);

  // Format damage with target health percentage
  const formatDamage = (damage: number, targetMaxHealth?: number) => {
    // Round damage to nearest integer
    const roundedDamage = Math.round(damage);
    
    // Calculate percentage of target's max health if available
    if (targetMaxHealth && targetMaxHealth > 0) {
      const percentage = (damage / targetMaxHealth) * 100;
      return (
        <div>
          <div className="damage-value">{roundedDamage}</div>
          <div className="damage-percentage">{percentage.toFixed(1)}%</div>
        </div>
      );
    }
    
    // Fallback to just rounded damage
    return roundedDamage;
  };

  // Reset handler functions using WebSocket
  const handleFullReset = () => {
    if (!socket) {
      console.warn('[CustomComboSection] WebSocket not available for full reset');
      return;
    }

    console.log('[CustomComboSection] Full reset - both champions + clear combo');
    
    // Reset primary champion
    const primaryResetData = {
      championId: champion.id,
      championName: champion.name
    };
    socket.sendGameMessage('RESET_CHAMPION_COMBO', primaryResetData);
    
    // Reset opponent champion if available
    if (targetChampion) {
      const opponentResetData = {
        championId: targetChampion.id,
        championName: targetChampion.name
      };
      socket.sendGameMessage('RESET_CHAMPION_COMBO', opponentResetData);
    }
    
    // Clear combo sequence
    if (onResetCombo) {
      onResetCombo();
    }
  };

  const handleResetBlueTeam = () => {
    if (!socket) {
      console.warn('[CustomComboSection] WebSocket not available for blue team reset');
      return;
    }

    // Find which champion is on blue team
    const blueChampion = champion.team === 'blue' ? champion : targetChampion;
    
    if (!blueChampion) {
      console.warn('[CustomComboSection] No blue team champion found');
      return;
    }

    console.log('[CustomComboSection] Resetting blue team champion:', blueChampion.name);
    
    const resetData = {
      championId: blueChampion.id,
      championName: blueChampion.name
    };
    socket.sendGameMessage('RESET_CHAMPION_COMBO', resetData);
  };

  const handleResetRedTeam = () => {
    if (!socket) {
      console.warn('[CustomComboSection] WebSocket not available for red team reset');
      return;
    }

    // Find which champion is on red team
    const redChampion = champion.team === 'red' ? champion : targetChampion;
    
    if (!redChampion) {
      console.warn('[CustomComboSection] No red team champion found');
      return;
    }

    console.log('[CustomComboSection] Resetting red team champion:', redChampion.name);
    
    const resetData = {
      championId: redChampion.id,
      championName: redChampion.name
    };
    socket.sendGameMessage('RESET_CHAMPION_COMBO', resetData);
  };

  const handleClearCombo = () => {
    if (!socket) {
      console.warn('[CustomComboSection] WebSocket not available for clearing combo logs');
      return;
    }

    console.log('[CustomComboSection] Clearing combo logs for champion:', champion.name);
    
    // Send the new clear logs message that only clears logs for this champion
    socket.sendGameMessage('CLEAR_COMBO_LOGS', {
      championId: champion.id,
      championName: champion.name
    });
  };

  return (
    <div className="custom-combo-section">
      {/* Combat Display Section - always shown */}

         {/* Reset Buttons integrated into combat display */}
         <div className="reset-buttons-in-combat">
          <button 
            className="reset-btn full-reset"
            onClick={handleFullReset}
            title="Reset both champions"
          >
            🔄
          </button>
          <button 
            className="reset-btn reset-blue-team"
            onClick={handleResetBlueTeam}
            title="Reset blue side champion"
          >
            🔵
          </button>
          <button 
            className="reset-btn reset-red-team"
            onClick={handleResetRedTeam}
            title="Reset red side champion"
          >
            🔴
          </button>
        </div>
      <div className="combo-abilities-section">
        <CombatDisplay
          champion={champion}
          targetChampion={targetChampion}
          logs={logs}
          spellImageUrls={spellImageUrls}
          autoAttackImageUrl={autoAttackImageUrl}
          passiveImageUrl={passiveImageUrl}
          championImageUrl={championImageUrl || '/api/placeholder/64/64'}
          targetChampionImageUrl={targetChampionImageUrl || '/api/placeholder/64/64'}
        />
        
     
        
        <AbilityButtons
          championName={champion.name}
          spellImageUrls={spellImageUrls}
          autoAttackImageUrl={autoAttackImageUrl}
          passiveImageUrl={passiveImageUrl}
          abilities={champion.abilities}
          onAbilityClick={onAbilityClick}
        />
        
        {/* Cooldowns Table - restored to full width */}
        <CooldownDebugTable cooldowns={champion.cooldowns || []} />
      </div>

      {/* Custom Combo Display using same styling as StaticSimpleMatchupDisplay */}
      {abilitySequenceString.length > 0 ? (
        <div className="combo-section">
          <div className="combo-item">
            <div 
              className="combo-header"
              onClick={() => setShowComboBreakdown(!showComboBreakdown)}
            >
              <h4>
                <span className="expand-icon">
                  {showComboBreakdown ? '▼' : '►'}
                </span>
                <MiniAbilityIcons sequence={abilitySequenceString} championName={champion.name} events={logs} />
                {/* Don't show combo name for multi-ability combos like the static version */}
              </h4>
              <span className="combo-damage">{formatDamage(damageData.total, targetChampion?.stats.totalStats.health)}</span>
            </div>
            
            {showComboBreakdown && (
              <div className="combo-events">
                <ComboDisplay
                  championName={champion.name}
                  logs={logs}
                  showDetails={true}
                  showStackingEvents={true}
                  showStatEvents={true}
                  showCCEvents={true}
                  targetChampionName={targetChampion?.name}
                  targetMaxHealth={targetChampion?.stats.totalStats.health || 0}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Placeholder text when no combo - not clickable */
        <div className="combo-placeholder sub-dropdown">
          <span className="combo-placeholder-text">Use your keyboard or click the ability buttons Q W E R A above to create a combo</span>
        </div>
      )}
    </div>
  );
};

export default CustomComboSection; 